import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Doctor } from '../_models/doctor.model';
import { QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { AuthService, currentAuthToken, currentUser, User } from '../../auth';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';

@Injectable({ providedIn: 'root' })
export class DoctorService {
  constructor(
    private httpClient: HttpClient,
    private snackBar: MatSnackBar,
    private auth: AuthService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  currentUser: Observable<User>;
  authToken: any;
  companyId;

  private static getDoctorsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;

    let fromString = ``;
    if (!filter.isActive && sortField !== 'isActive') fromString = `sort=isActive,desc&`;
    fromString += `page=${pageNumber}&size=${pageSize}${
      sortField.localeCompare('name') === 0
        ? `&sort=firstName,${sortOrder}&sort=middleName,${sortOrder}&sort=lastName,${sortOrder}&sort=lastName2,${sortOrder}`
        : `&sort=${sortField},${sortOrder}`
    }`;

    if (filter.searchTerm) {
      fromString += `&search_term=${encodeURIComponent(filter.searchTerm)}&is_active=${filter.isActive}`;
    } else {
      if (filter.id) fromString += `&id_has=${encodeURIComponent(filter.id)}`;
      if (filter.nameEmail)
        fromString += /^[^\s@]*@[^\s@]*$/.test(encodeURIComponent(filter.nameEmail))
          ? `&email_has=${encodeURIComponent(filter.nameEmail)}`
          : `&name_has=${encodeURIComponent(filter.nameEmail)}`;
      if (filter.npi) fromString += `&npi_has=${filter.npi}`;
      if (filter.sln) fromString += `&sln_has=${filter.sln}`;
      if (filter.officePhone) fromString += `&phone_has=${encodeURIComponent(filter.officePhone)}`;
      if (filter.fax) fromString += `&fax_has=${encodeURIComponent(filter.fax)}`;
      if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
      if (filter.officeId) fromString += `&office_id=${encodeURIComponent(filter.officeId)}`;
    }

    return fromString;
  }

  createDoctor(_doctor: Doctor) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body = {
      office_id: _doctor.office ? _doctor.office.id : _doctor.office_id,
      doctor_id: _doctor.doctor_id,
      last_name: _doctor.last_name,
      middle_name: _doctor.middle_name,
      last_name2: _doctor.last_name2,
      first_name: _doctor.first_name,
      email: _doctor.email,
      fax: _doctor.fax,
      note: _doctor.note,
      is_active: _doctor.is_active,
      processed: _doctor.processed,
      office_phone: _doctor.office_phone,
      cell_phone: _doctor.cell_phone,
      dea: _doctor.dea,
      npi: _doctor.npi,
      sln: _doctor.sln,
      address_1: _doctor.address_1,
      address_2: _doctor.address_2,
      city: _doctor.city,
      state: _doctor.state,
      zip: _doctor.zip,
      urbanization: _doctor.urbanization,
      tags: _doctor.tags,
      affiliation_groups: _doctor.affiliation_groups !== null ? _doctor.affiliation_groups : null,
      gender: _doctor.gender,
      avatar: _doctor.avatar,
      extension: _doctor.extension,
    };
    return this.httpClient.post<Doctor>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/doctors',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  deleteDoctor(id?: number) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.httpClient.delete<any>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/doctors/' + id,
      { headers: httpHeaders },
    );
  }

  updateDoctor(_doctor: Doctor): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body = {
      address_1: _doctor.address_1,
      address_2: _doctor.address_2,
      cell_phone: _doctor.cell_phone,
      city: _doctor.city,
      dea: _doctor.dea,
      doctor_id: _doctor.doctor_id,
      email: _doctor.email,
      fax: _doctor.fax,
      first_name: _doctor.first_name,
      practice_name: _doctor.practice_name,
      id: _doctor.id,
      is_active: _doctor.is_active,
      last_name: _doctor.last_name,
      middle_name: _doctor.middle_name,
      last_name2: _doctor.last_name2,
      location_id: _doctor.location_id,
      note: _doctor.note,
      npi: _doctor.npi,
      office_id: _doctor.office_id,
      office_phone: _doctor.office_phone,
      processed: _doctor.processed,
      sln: _doctor.sln,
      state: _doctor.state,
      zip: _doctor.zip,
      urbanization: _doctor.urbanization,
      tags: _doctor.tags,
      affiliation_groups: _doctor.affiliation_groups !== null ? _doctor.affiliation_groups : null,
      plus_code: _doctor.plus_code,
      latitude: _doctor.latitude,
      longitude: _doctor.longitude,
      provider_types: _doctor.provider_types,
      gender: _doctor.gender,
      geolocation_address: _doctor.geolocation_address,
      avatar: _doctor.avatar,
      extension: _doctor.extension,
    };

    const id = _doctor.id;
    return this.httpClient.put(
      environment.CORE_BASE + '/companies/' + this.companyId + '/doctors/' + id,
      body,
      { headers: httpHeaders },
    );
  }

  updateDoctorStatus(doctor_id: number, is_active: boolean): Observable<any> {
    const body = {
      is_active: is_active,
    };
    return this.httpClient.put(
      environment.CORE_BASE + '/companies/' + this.companyId + '/entities/' + doctor_id + '/status',
      body,
    );
  }

  findDoctors(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: DoctorService.getDoctorsListQueryString(queryParams),
    });

    return this.httpClient.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/doctors/list-view`,
      { params },
    );
  }

  getDoctorsAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const params = new HttpParams({
      fromString: DoctorService.getDoctorsListQueryString(queryParams),
    });

    return this.httpClient.get(`${environment.CORE_BASE}/companies/${this.companyId}/doctors/csv`, {
      params,
      responseType: 'text',
    });
  }

  searchDoctors(term: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/doctors?sort=firstName,asc&search_term=' +
      term;
    return this.httpClient.get<any>(url, { headers });
  }

  updateDoctorImage(file: File, pid): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.companyId}/entities/${pid}/attachment`;
    return this.httpClient.post(url, { avatar: file.name }, { headers });
  }

  uploadDoctorImage(s3, contactFile: File): Observable<any> {
    const url = s3.data.url;
    return this.httpClient.put(url, contactFile, {});
  }
}
